import React, { useCallback, useState } from 'react'
import Box from '@mui/material/Box'
import StandardLayout from '../components/StandardLayout'
import XlContainer from '../components/XlContainer'
import ContactForm from 'views/ContactForm'
import Footer from 'views/Footer'
import useLogBreakpoints from 'hooks/useLogBreakpoints'
import { useTheme } from '@mui/material/styles'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { StaticImage } from 'gatsby-plugin-image'
import useIsMobile from 'hooks/useIsMobile'
import GetStartedNoTimeSection from 'views/GetStartedNoTimeSection'
import FintechExponentialImpactSection from 'views/FintechExponentialImpactSection'
import ShareImpactBreezeSection from 'views/ShareImpactBreezeSection'
import BuildLoyaltySection from 'views/BuildLoyaltySection'
import { graphql, useStaticQuery } from 'gatsby'
import Testimonial from '../components/Testimonial'
import GreyGradient from 'components/GreyGradient'
import Page, { PageBodyWithoutNav } from '../components/Page'
import { WindowLocation } from '@reach/router'
import NavBar from '../views/NavBar/NavBar'
import { RenderOnDesktop, RenderOnMobile } from '../components/DeviceUtils'
import FintechUseCasesSection from '../views/FintechUseCasesSection'
import FintechHero from '../images/FintechHero.png'
import HeroHeadingShared from '../views/HeroHeadingShared'

const stackSpacing = {
    xs: 10,
    sm: 15,
    md: 15,
    lg: 15,
    xl: 15,
}

const PaymentsPage = ({ location }: { location: WindowLocation }) => {
    useLogBreakpoints()
    const [navBackgroundColor, setNavBackgroundColor] = useState<string>('transparent')
    const { spacing } = useTheme()
    const isMobile = useIsMobile()

    const onNavBackgroundColorChange = useCallback(
        (color: string) => {
            setNavBackgroundColor(color)
        },
        [navBackgroundColor],
    )

    return (
        <Page location={location} title="Lune - Fintech & Banking">
            <PageBodyWithoutNav>
                <Box
                    sx={{
                        position: `sticky`,
                        top: `0px`,
                        zIndex: `9999`,
                        backgroundColor: `${navBackgroundColor}`,
                    }}
                >
                    <XlContainer maxWidth="xl">
                        <Box
                            px={{
                                xs: 1,
                                sm: 2,
                                md: 2,
                                lg: 2,
                                xl: 2,
                            }}
                        >
                            <StandardLayout>
                                <NavBar
                                    backgroundColor={'transparent'}
                                    onBackgroundChange={onNavBackgroundColorChange}
                                />
                            </StandardLayout>
                        </Box>
                    </XlContainer>
                </Box>

                <Box
                    sx={{
                        ...(!isMobile
                            ? {
                                  marginTop: `-${spacing(9)}`, // move up the height of NavBar
                              }
                            : {}),
                    }}
                >
                    <GreyGradient hide={isMobile}>
                        <Box
                            sx={{
                                zIndex: '0',
                                overflow: 'hidden',
                            }}
                            mb={{
                                xs: 10,
                                sm: 15,
                                md: 15,
                                lg: 15,
                                xl: 15,
                            }}
                        >
                            <HeroHeadingShared
                                imageUrl={FintechHero}
                                placeholderImageUrl={
                                    'https://assets.lune.co/lune-co/placeholders/fintech-header.svg'
                                }
                            >
                                {' '}
                                <Box
                                    pt={{
                                        xs: 9,
                                        sm: 18,
                                        md: 18,
                                        lg: 18,
                                        xl: 18,
                                    }}
                                >
                                    <StandardLayout>
                                        <XlContainer maxWidth="xl">
                                            <RenderOnMobile>
                                                <Typography
                                                    variant={`h2`}
                                                    component="div"
                                                    sx={{ textAlign: `left` }}
                                                >
                                                    Become the catalyst for your customer’s climate
                                                    journey
                                                </Typography>
                                            </RenderOnMobile>
                                            <RenderOnDesktop>
                                                <Typography
                                                    variant={`h2`}
                                                    component="div"
                                                    sx={{ textAlign: `center` }}
                                                >
                                                    Become the catalyst for
                                                </Typography>
                                                <Typography
                                                    variant={`h2`}
                                                    component="div"
                                                    sx={{ textAlign: `center` }}
                                                >
                                                    your customer’s climate journey
                                                </Typography>
                                            </RenderOnDesktop>
                                        </XlContainer>
                                    </StandardLayout>
                                </Box>
                            </HeroHeadingShared>
                        </Box>
                    </GreyGradient>

                    <Stack spacing={stackSpacing} sx={{ overflow: 'hidden' }}>
                        <Box sx={{ width: '100%' }}>
                            <XlContainer>
                                <Stack spacing={stackSpacing}>
                                    <GreyGradient hide={!isMobile}>
                                        <StandardLayout>
                                            <FintechExponentialImpactSection />
                                        </StandardLayout>
                                    </GreyGradient>

                                    <GreyGradient hide={!isMobile}>
                                        <StandardLayout>
                                            <FintechUseCasesSection />
                                        </StandardLayout>
                                    </GreyGradient>

                                    <GreyGradient hide={!isMobile}>
                                        <StandardLayout>
                                            <BuildLoyaltySection />
                                        </StandardLayout>
                                    </GreyGradient>

                                    <GreyGradient hide={!isMobile}>
                                        <StandardLayout>
                                            <GetStartedNoTimeSection />
                                        </StandardLayout>
                                    </GreyGradient>

                                    <GreyGradient hide={!isMobile}>
                                        <StandardLayout>
                                            <ShareImpactBreezeSection />
                                        </StandardLayout>
                                    </GreyGradient>

                                    <StandardLayout>
                                        <Testimonial
                                            text={
                                                'We chose Lune because of the great software developer experience & carbon project selection. Now we are able to offer our customers trustworthy carbon offsetting as a core part of our value proposition.'
                                            }
                                            profileName={'Peter Kirby'}
                                            profileTitle={'Co-founder at Tred'}
                                            profileAvatar={
                                                <StaticImage
                                                    src="../images/PeterKirby.png"
                                                    alt={`Peter Kirby`}
                                                />
                                            }
                                            textColor={'White'}
                                            backgroundImageStaticQuery={useStaticQuery(graphql`
                                                query {
                                                    desktop: file(
                                                        relativePath: {
                                                            eq: "TestimonialProduct.png"
                                                        }
                                                    ) {
                                                        childImageSharp {
                                                            fluid(quality: 90, maxWidth: 1920) {
                                                                ...GatsbyImageSharpFluid_withWebp
                                                            }
                                                        }
                                                    }
                                                }
                                            `)}
                                            whiteQuote
                                        />
                                    </StandardLayout>

                                    <StandardLayout>
                                        <ContactForm
                                            hubspotFormName={'payments'}
                                            headers={['Start building your competitive advantage']}
                                        />
                                    </StandardLayout>
                                    <Footer />
                                </Stack>
                            </XlContainer>
                        </Box>
                    </Stack>
                </Box>
            </PageBodyWithoutNav>
        </Page>
    )
}

export default PaymentsPage
